import React, { useState } from "react";
import { User } from "../../../Entities/User";
import useCandidateResumes from "../../../Hooks/useCandidateResumes";
import { Empty, Pagination } from "antd";
import CandidateResumeCard from "./CandidateResumeCard";
import { UserProfile } from "../../../Entities/UserProfile";
import CandidateResumePreview from "../../../Components/CandidateResumePreview";
import { openNotification } from "../../../Components/Notification";
import { Loading3QuartersOutlined } from "@ant-design/icons";

interface CandidateResumesProps {
  candidate: User;
}

const CandidateResumes: React.FC<CandidateResumesProps> = ({ candidate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedResume, setSelectedResume] = useState<UserProfile | null>(
    null,
  );
  const { loading, pagination, userProfiles, handlePageChange } =
    useCandidateResumes(candidate._id);

  const handleViewResume = (resume: UserProfile) => {
    setIsOpen(true);
    setSelectedResume(resume);
  };

  const handleCloseResume = () => {
    setIsOpen(false);
    setSelectedResume(null);
  };

  const handleDownloadResume = async (resume: UserProfile) => {
    try {
      const url = `https://hiring-assistant-files.s3.ap-south-1.amazonaws.com/profile/${resume.userId}/${resume.filename}`;

      // Fetch the file as a blob to handle CORS properly
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to download file");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger download
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = resume.filename || "resume.pdf";
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading resume:", error);
      openNotification(
        "error",
        "Download Failed",
        "Failed to download the resume. Please try again.",
      );
    }
  };

  return (
    <div
      className={`candidate-details-tab-section candidate-details-resume-section${loading ? " is-loading" : ""}`}
    >
      {loading && (
        <div className="candidate-details-resume-section__loader">
          <Loading3QuartersOutlined spin={loading} />
        </div>
      )}
      {userProfiles.length > 0 ? (
        <div className="user-profiles-list hide-scroll-bar">
          {userProfiles.map((up, i) => (
            <CandidateResumeCard
              userProfile={up}
              key={i}
              handleViewResume={handleViewResume}
              handleDownloadResume={handleDownloadResume}
            />
          ))}
        </div>
      ) : (
        <div>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="There are no resumes found for this user"
          />
        </div>
      )}
      {userProfiles.length > 0 && (
        <div className="user-profiles-list-pagination">
          <Pagination
            current={pagination.page}
            pageSize={pagination.limit}
            total={pagination.totalRecords}
            showSizeChanger={false}
            showTotal={(total, range) =>
              `Showing ${range[0]}–${range[1]} of ${total} resume${total !== 1 ? "s" : ""}`
            }
            onChange={handlePageChange}
          />
        </div>
      )}

      {selectedResume && (
        <CandidateResumePreview
          isOpen={isOpen}
          userProfile={selectedResume}
          onClose={handleCloseResume}
        />
      )}
    </div>
  );
};

export default CandidateResumes;
